import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getLicenceProvider, licenceBadgeText } from './licenceProvider';
import { translateWorkType } from './i18n';
import { displayNumber } from './numbering';

const money = (n, lang) => {
  const v = (Number(n) || 0).toFixed(2);
  return lang === 'en' ? `$${v}` : `${v} $`;
};

// Brand color: deep bordeaux (#7A1F2B)
const BRAND = [122, 31, 43];
const BRAND_LIGHT = [250, 247, 242];
const ACCENT = [201, 169, 97]; // champagne gold

async function loadImageAsDataUrl(url) {
  try {
    const res = await fetch(url, { mode: 'cors' });
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn('Image load failed', e);
    return null;
  }
}

function inferImageFormat(dataUrl) {
  const m = /^data:image\/(\w+);/.exec(dataUrl || '');
  if (!m) return 'PNG';
  const f = m[1].toLowerCase();
  if (f === 'jpeg' || f === 'jpg') return 'JPEG';
  if (f === 'webp') return 'WEBP';
  return 'PNG';
}

function drawHeaderBand(doc, pageW) {
  // PREMIUM MINIMALIST — White header, thin gold rule for elegance
  // Top accent: very thin bordeaux line at the absolute top edge (subtle brand cue)
  doc.setFillColor(...BRAND);
  doc.rect(0, 0, pageW, 3, 'F');
}

function drawFooter(doc, pageW, pageH, profile, t, pageNum, totalPages) {
  const margin = 40;
  const footerY = pageH - 56;
  // Top divider line (gold)
  doc.setDrawColor(...ACCENT);
  doc.setLineWidth(0.8);
  doc.line(margin, footerY, pageW - margin, footerY);

  // Row 1: company + licence on the left, page number on the right
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...BRAND);
  const lic = getLicenceProvider(profile?.province);
  const licPrefix = lic.short === 'Licence' ? 'Licence' : lic.short;
  const line1 = [
    profile?.company_name,
    profile?.rbq_number ? `${licPrefix} ${profile.rbq_number}` : null,
  ].filter(Boolean).join('  ·  ');
  doc.text(line1 || 'SoumissionPro', margin, footerY + 14);

  // Page number — RIGHT side, same baseline as company (line 1)
  if (totalPages && totalPages > 1) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...BRAND);
    doc.text(`${pageNum} / ${totalPages}`, pageW - margin, footerY + 14, { align: 'right' });
  }

  // Row 2: contact info on the left, "Généré avec" on the right (line 2)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(110);
  const line2 = [profile?.phone, profile?.email, profile?.address]
    .filter(Boolean)
    .join('  ·  ');
  if (line2) {
    // Truncate to avoid horizontal overlap with the right-aligned label
    const maxLeftWidth = pageW - margin * 2 - 140; // leave 140pt for the right label
    let truncated = line2;
    while (doc.getTextWidth(truncated) > maxLeftWidth && truncated.length > 10) {
      truncated = truncated.slice(0, -4) + '…';
    }
    doc.text(truncated, margin, footerY + 28);
  }

  doc.setFontSize(7);
  doc.setTextColor(150);
  doc.text('Généré avec SoumissionPro', pageW - margin, footerY + 28, { align: 'right' });
}

function drawWatermark(doc, pageW, pageH, dataUrl, fmt) {
  if (!dataUrl) return;
  try {
    const size = 280;
    const x = (pageW - size) / 2;
    const y = (pageH - size) / 2;
    // jsPDF GState for transparency
    if (doc.GState) {
      const gState = new doc.GState({ opacity: 0.06 });
      doc.setGState(gState);
      doc.addImage(dataUrl, fmt, x, y, size, size, undefined, 'FAST');
      doc.setGState(new doc.GState({ opacity: 1 }));
    }
  } catch (e) { console.warn('[pdf] watermark draw failed:', e); }
}

function formatPdfDate(value, lang) {
  if (!value) return '—';
  const raw = String(value);
  const d = /^\d{4}-\d{2}-\d{2}/.test(raw)
    ? new Date(`${raw.slice(0, 10)}T12:00:00`)
    : new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return lang === 'en'
    ? d.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })
    : d.toLocaleDateString('fr-CA');
}

/**
 * PDF facture — même gabarit graphique que la soumission.
 * opts: preview | print | returnBlob | returnDoc (identiques à generatePDF)
 */
export async function generateInvoicePDF(inv, profile, t, opts = {}) {
  const subLike = {
    ...inv,
    submission_number: inv.invoice_number,
    invoice_number: inv.invoice_number,
    created_at: inv.invoice_date || inv.created_at,
    expires_at: inv.due_date || null,
    photos: [],
    signature_data_url: null,
    signed_at: null,
    signed_by_name: null,
  };
  return generatePDF(subLike, profile, t, { ...opts, documentType: 'invoice' });
}

export async function generatePDF(sub, profile, t, opts = {}) {
  try {
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 40;
    const lang = opts.lang === 'en' ? 'en' : 'fr';
    const isInvoice = opts.documentType === 'invoice';
    const rawNumber = isInvoice ? (sub.invoice_number || sub.submission_number) : sub.submission_number;
    const docNumber = displayNumber(rawNumber, t);
    const fileBase = rawNumber || (isInvoice ? 'facture' : 'soumission');

    let logoDataUrl = null;
    let logoFmt = 'PNG';
    if (profile?.logo_url) {
      logoDataUrl = await loadImageAsDataUrl(profile.logo_url);
      if (logoDataUrl) logoFmt = inferImageFormat(logoDataUrl);
    }

    // === HEADER BAND ===
    drawHeaderBand(doc, pageW);

    // === HEADER — LUXURY MAGAZINE / WHITE BACKGROUND ===
    // Logo top-left (refined size)
    if (logoDataUrl) {
      try {
        doc.addImage(logoDataUrl, logoFmt, margin, 36, 52, 52, undefined, 'FAST');
      } catch (e) { console.warn('[pdf] logo image add failed:', e); }
    }
    const titleX = logoDataUrl ? margin + 70 : margin;

    // Eyebrow tag SOUMISSION / FACTURE
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...BRAND);
    const eyebrow = isInvoice
      ? (t('pdf.invoice') || 'FACTURE')
      : (t('pdf.quote') || 'SOUMISSION');
    doc.text(String(eyebrow).toUpperCase(), titleX, 50, { charSpace: 3.5 });

    // HUGE document number — Times serif for luxury feel (the hero element)
    doc.setFont('times', 'bold');
    doc.setFontSize(32);
    doc.setTextColor(...BRAND);
    doc.text(docNumber, titleX, 78);

    // Company name — refined serif italic subtitle
    doc.setFont('times', 'italic');
    doc.setFontSize(11);
    doc.setTextColor(80);
    doc.text(profile?.company_name || '', titleX, 94);

    // === Right block: dates & licence — minimalist columns ===
    // === Right block: dates & licence — perfectly right-aligned columns ===
    // Helper to right-align labels (NO charSpace to ensure pixel-perfect alignment with values)
    const rightX = pageW - margin;
    const drawRightLabel = (text, y) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...ACCENT);
      doc.text(text, rightX, y, { align: 'right' });
    };
    const drawRightValue = (text, y) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(40);
      doc.text(text, rightX, y, { align: 'right' });
    };

    // Date column — labels + values share the EXACT same right edge
    if (isInvoice) {
      drawRightLabel((t('pdf.invoice_date') || t('pdf.date') || 'DATE').toUpperCase(), 50);
      drawRightValue(formatPdfDate(sub.invoice_date || sub.created_at, lang), 64);
      if (sub.due_date || sub.expires_at) {
        drawRightLabel((t('pdf.due_date') || 'ÉCHÉANCE').toUpperCase(), 80);
        drawRightValue(formatPdfDate(sub.due_date || sub.expires_at, lang), 94);
      }
    } else {
      drawRightLabel((t('pdf.date') || 'DATE').toUpperCase(), 50);
      drawRightValue(formatPdfDate(sub.created_at || new Date().toISOString(), lang), 64);

      // Validité
      if (sub.expires_at) {
        drawRightLabel((t('pdf.valid_until') || 'VALIDE JUSQU\u2019AU').toUpperCase(), 80);
        drawRightValue(formatPdfDate(sub.expires_at, lang), 94);
      }
    }

    // Thin gold rule SEPARATOR under the header (the signature luxury element)
    doc.setDrawColor(...ACCENT);
    doc.setLineWidth(0.5);
    doc.line(margin, 110, pageW - margin, 110);
    // Tiny secondary bordeaux rule just under (refined double-line effect)
    doc.setDrawColor(...BRAND);
    doc.setLineWidth(0.2);
    doc.line(margin, 113, pageW - margin, 113);

    // === END HEADER ===

    // PREMIUM "VÉRIFIÉ" MEDALLION — Serif luxury seal, gold double-frame
    if (profile?.rbq_verified && profile?.rbq_number) {
      const badgeY = 130;
      const lic = getLicenceProvider(profile?.province);
      const licPrefix = lic.short === 'Licence' ? 'Licence' : lic.short;
      const licNumber = String(profile.rbq_number).trim();
      const certifiedLabel = (t && t('pdf.certified_verified')) || 'CERTIFIÉ \u00B7 VÉRIFIÉ';

      // Mesure les deux lignes au MAX (bold est légèrement plus large) pour garantir l'espace
      doc.setFont('times', 'bold');
      doc.setFontSize(11);
      const prefixW = doc.getTextWidth(licPrefix);
      doc.setFont('times', 'normal');
      const numW = doc.getTextWidth(licNumber);
      // Mesure aussi le label "CERTIFIÉ · VÉRIFIÉ" pour qu'il rentre aussi
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6);
      const labelW = doc.getTextWidth(certifiedLabel) + (6 * 2); // +charSpace contribution
      const innerW = Math.max(numW, prefixW, labelW);

      const padX = 16;       // padding gauche interne
      const rightPadX = 20;  // padding droite interne
      const sealSize = 28;   // médaillon plus compact (avant 32)
      const sealTextGap = 12;
      const badgeW = innerW + padX + sealSize + sealTextGap + rightPadX;
      const badgeH = 42;     // ↓ hauteur réduite (avant 52) — gagne 10pt vertical
      const badgeX = pageW - margin - badgeW;

      // Outer gold double frame (the luxury hallmark)
      doc.setDrawColor(...ACCENT);
      doc.setLineWidth(1.4);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(badgeX, badgeY, badgeW, badgeH, 5, 5, 'FD');
      // Inner thin gold frame (luxury detail)
      doc.setLineWidth(0.3);
      doc.roundedRect(badgeX + 3, badgeY + 3, badgeW - 6, badgeH - 6, 3, 3, 'D');

      // Seal medallion (left side, vertically centered in badge)
      const sealCx = badgeX + padX + sealSize / 2;
      const sealCy = badgeY + badgeH / 2;
      const sealR = sealSize / 2;
      // Outer gold ring
      doc.setFillColor(...ACCENT);
      doc.circle(sealCx, sealCy, sealR, 'F');
      // Inner cream ring
      doc.setFillColor(252, 250, 244);
      doc.circle(sealCx, sealCy, sealR - 2, 'F');
      // Inner EMERALD GREEN core
      doc.setFillColor(22, 128, 90);
      doc.circle(sealCx, sealCy, sealR - 5, 'F');
      // Check mark — clean polyline
      doc.setDrawColor(255);
      doc.setLineWidth(2.0);
      doc.setLineCap('round');
      doc.setLineJoin('round');
      doc.line(sealCx - 5, sealCy + 0.5, sealCx - 1.5, sealCy + 4);
      doc.line(sealCx - 1.5, sealCy + 4, sealCx + 5, sealCy - 3.5);
      doc.setLineCap('butt');

      // Text block (right side) — 3 lignes compactes
      const textX = sealCx + sealR + sealTextGap;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6);
      doc.setTextColor(...ACCENT);
      doc.text(certifiedLabel, textX, badgeY + 10);
      // Licence prefix in serif (bold)
      doc.setFont('times', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...BRAND);
      doc.text(licPrefix, textX, badgeY + 22);
      // Numéro de licence
      doc.setFont('times', 'normal');
      doc.text(licNumber, textX, badgeY + 34);
    }

    // === WATERMARK (subtle logo behind content) ===
    drawWatermark(doc, pageW, pageH, logoDataUrl, logoFmt);

    // Reset to body
    doc.setTextColor(40);

    // === PARTIES (FROM / TO / RBQ BADGE) — 3-column premium layout ===
    // FROM and TO sit on the LEFT/MIDDLE columns at the SAME Y as the RBQ
    // badge on the right. This uses every pixel of horizontal space and gives
    // the document the polished feel of premium SaaS invoices.
    const hasBadge = !!(profile?.rbq_verified && profile?.rbq_number);

    // Approx badge width (matches the calc in the badge-drawing block above).
    // We re-compute conservatively: same padX/sealSize/sealTextGap/rightPadX
    // values so the FROM/TO cards never overlap the badge.
    const approxBadgeW = hasBadge ? 200 : 0;
    const partiesY = hasBadge ? 130 : 128;
    // Available horizontal space LEFT of the badge for FROM/TO cards.
    const leftZoneW = pageW - margin - margin - (hasBadge ? (approxBadgeW + 16) : 0);
    const colW = (leftZoneW - 16) / 2; // 2 columns separated by 16pt gutter

    function drawPartyCard(x, yStart, title, lines, maxW) {
      // Ultra-minimalist: white background, NO box. Just a tiny eyebrow label + serif content.
      // Eyebrow label (uppercase, small, bordeaux, letter-spaced)
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(...BRAND);
      doc.text((title || '').toUpperCase(), x, yStart + 8, { charSpace: 2.5 });
      // Thin gold rule under label (luxury accent)
      doc.setDrawColor(...ACCENT);
      doc.setLineWidth(0.4);
      doc.line(x, yStart + 12, x + 30, yStart + 12);
      // Content lines in serif Times — first line bigger (the name)
      doc.setFont('times', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(40);
      const firstLine = lines[0] ? String(lines[0]) : '';
      const firstWrapped = doc.splitTextToSize(firstLine, maxW);
      doc.text(firstWrapped, x, yStart + 24);
      // Detail lines wrap if they exceed the column width (no truncation).
      doc.setFont('times', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(80);
      let lineY = yStart + 24 + firstWrapped.length * 13;
      lines.slice(1).forEach((l) => {
        const wrapped = doc.splitTextToSize(String(l), maxW);
        wrapped.forEach((w) => {
          doc.text(w, x, lineY);
          lineY += 11;
        });
      });
      return lineY - yStart; // total height of the card content
    }

    const lic2 = getLicenceProvider(profile?.province);
    const licPrefix2 = lic2.short === 'Licence' ? 'Licence' : lic2.short;
    const fromLines = [
      profile?.company_name || '',
      profile?.rbq_number ? `${licPrefix2} ${profile.rbq_number}` : null,
      profile?.business_number
        ? `${t('pdf.business_number') || 'BN'} ${profile.business_number}`
        : null,
      profile?.gst_hst_number
        ? `${t('pdf.gst_hst_number') || 'TPS/TVH'} ${profile.gst_hst_number}`
        : null,
      profile?.qst_number
        ? `${t('pdf.qst_number') || 'TVQ'} ${profile.qst_number}`
        : null,
      profile?.address || '',
      profile?.phone || '',
      profile?.email || '',
    ].filter(Boolean);
    const toLines = [
      sub.client_name || '',
      sub.client_address || '',
      sub.client_phone || '',
      sub.client_email || '',
    ].filter(Boolean);

    const hFrom = drawPartyCard(margin, partiesY, t('pdf.from') || 'DE', fromLines, colW);
    const hTo = drawPartyCard(margin + colW + 16, partiesY, t('pdf.to') || 'POUR', toLines, colW);

    // Move y to just below the tallest of the three blocks (FROM / TO / BADGE).
    // Badge bottom = partiesY + 42 (badgeH). Add a 14pt breathing-room gap.
    const partiesBottom = partiesY + Math.max(hFrom, hTo, hasBadge ? 42 : 0);
    let y = partiesBottom + 14;

    // Invoice: reference to source quote + job site address
    if (isInvoice && sub.source_submission_number) {
      const ref = (t('pdf.invoice_ref_submission') || 'Facture générée à partir de la soumission {num}')
        .replace('{num}', sub.source_submission_number);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(90);
      const refLines = doc.splitTextToSize(ref, pageW - margin * 2);
      doc.text(refLines, margin, y);
      y += refLines.length * 11 + 6;
    }
    if (sub.site_address && String(sub.site_address).trim()
      && String(sub.site_address).trim() !== String(sub.client_address || '').trim()) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...BRAND);
      doc.text((t('invoice.site_address') || 'Chantier').toUpperCase(), margin, y, { charSpace: 1.5 });
      doc.setFont('times', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(40);
      const siteLines = doc.splitTextToSize(String(sub.site_address).trim(), pageW - margin * 2);
      doc.text(siteLines, margin, y + 12);
      y += 12 + siteLines.length * 12 + 6;
    }

    // Work type chip — refined: subtle gold border + light tinted bg
    if (sub.work_type) {
      // Translate work_type to the active UI language so the PDF header
      // matches the language the client sees on screen.
      const pdfLang = (typeof document !== 'undefined' && document.documentElement?.lang) || (typeof localStorage !== 'undefined' && localStorage.getItem('lang')) || 'fr';
      const localizedWorkType = translateWorkType(sub.work_type, pdfLang);
      doc.setFillColor(250, 245, 230);
      doc.setDrawColor(...ACCENT);
      doc.setLineWidth(0.8);
      doc.roundedRect(margin, y, 240, 20, 4, 4, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(...BRAND);
      doc.text(`${(t('work.type') || 'Type').toUpperCase()}: ${localizedWorkType.toUpperCase()}`, margin + 10, y + 13.5);
      y += 24;
    }

    doc.setTextColor(40);

    // === MATERIALS ===
    const materials = Array.isArray(sub.materials) ? sub.materials : [];
    if (materials.length > 0) {
      const body = [];
      materials.forEach((m) => {
        body.push([
          m.description || '',
          String(m.quantity || 0),
          m.unit || '',
          money(m.unit_price, lang),
          money((Number(m.quantity) || 0) * (Number(m.unit_price) || 0), lang),
        ]);
        if (m.note && m.note.trim()) {
          body.push([{
            content: `> ${m.note.trim()}`,
            colSpan: 5,
            styles: { fontStyle: 'italic', textColor: [100, 100, 100], fontSize: 8, cellPadding: { top: 2, bottom: 4, left: 12, right: 6 } },
          }]);
        }
      });
      autoTable(doc, {
        startY: y,
        head: [[t('pdf.description') || 'Description', t('pdf.qty') || 'Qté', t('pdf.unit') || 'Unité', t('pdf.price') || 'Prix', t('pdf.total') || 'Total']],
        body,
        styles: { fontSize: 9, cellPadding: { top: 5, right: 8, bottom: 5, left: 10 }, lineColor: [230, 222, 200], lineWidth: 0.3 },
        headStyles: { fillColor: [250, 246, 238], textColor: BRAND, fontStyle: 'bold', fontSize: 8, cellPadding: { top: 6, right: 8, bottom: 6, left: 10 } },
        alternateRowStyles: { fillColor: [253, 251, 246] },
        columnStyles: { 1: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
        margin: { left: margin, right: margin },
        didDrawPage: () => drawWatermark(doc, pageW, pageH, logoDataUrl, logoFmt),
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    // === LABOUR ===
    const s = sub.summary || {};
    if (s.labourTotal > 0) {
      const lab = sub.labour || {};
      let labRows = [];
      if (lab.uniform === false && Array.isArray(lab.individuals) && lab.individuals.length > 0) {
        labRows = lab.individuals.map((w, idx) => {
          const wTotal = (Number(w.rate) || 0) * (Number(w.hours) || 0);
          const desc = `${w.name || ((t('lab.worker_n') || 'Ouvrier') + ' ' + (idx + 1))} — ${w.hours || 0}h @ ${money(w.rate, lang)}`;
          return [desc, money(wTotal, lang)];
        });
      } else {
        labRows = [[
          `${lab.workers || 0} × ${lab.hours || 0}h @ ${money(lab.hourly_rate, lang)}`,
          money(s.labourTotal, lang),
        ]];
      }
      autoTable(doc, {
        startY: y,
        head: [[t('pdf.labour') || 'Main-d\u2019\u0153uvre', '']],
        body: labRows,
        styles: { fontSize: 9, cellPadding: { top: 5, right: 8, bottom: 5, left: 10 }, lineColor: [230, 222, 200], lineWidth: 0.3 },
        headStyles: { fillColor: [250, 246, 238], textColor: BRAND, fontStyle: 'bold', fontSize: 8 },
        alternateRowStyles: { fillColor: [253, 251, 246] },
        columnStyles: { 1: { halign: 'right' } },
        margin: { left: margin, right: margin },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    // === TOTALS — Premium summary panel ===
    // The margin is intentionally folded into displayMaterials / displayLabour
    // so the client never sees the contractor's profit margin (commercially
    // sensitive). Math still adds up exactly: displayMaterials + displayLabour
    // + miscFees = subtotal.
    const dispMat = s.displayMaterials != null ? s.displayMaterials : s.materialsTotal;
    const dispLab = s.displayLabour != null ? s.displayLabour : s.labourTotal;
    const rows = [];
    if (dispMat > 0) rows.push([t('sum.materials') || 'Matériaux', money(dispMat, lang)]);
    if (dispLab > 0) rows.push([t('sum.labour') || 'Main-d\u2019\u0153uvre', money(dispLab, lang)]);
    if (s.miscFees) rows.push([t('sum.misc') || 'Frais divers', money(s.miscFees, lang)]);
    rows.push([t('sum.subtotal') || 'Sous-total', money(s.subtotal, lang)]);
    (s.taxes || []).forEach((tx) => rows.push([tx.label, money(tx.amount, lang)]));

    // We render the TOTALS table on the RIGHT half, and the GRAND TOTAL hero
    // card on the LEFT half at the same Y. This uses the empty left column
    // (otherwise wasted) and removes the need for a page break in most
    // small/medium quotes — exactly what pro tools like Jobber/QuickBooks do.
    const totalsStartY = y;
    autoTable(doc, {
      startY: y,
      body: rows,
      styles: { fontSize: 9.5, cellPadding: { top: 3.5, right: 8, bottom: 3.5, left: 12 }, lineColor: [240, 234, 218], lineWidth: 0.2 },
      columnStyles: {
        0: { halign: 'left', textColor: [110, 110, 110] },
        1: { halign: 'right', fontStyle: 'bold', textColor: [40, 40, 40] },
      },
      margin: { left: pageW / 2, right: margin },
    });

    const totalsEndY = doc.lastAutoTable.finalY;

    // === GRAND TOTAL — Hero card on the LEFT half, aligned with totals ===
    const totalH = 54;
    const totalW = pageW / 2 - margin - 6; // left-half width, minus a 6pt gutter
    const totalX = margin;

    // Compute the ideal Y so it visually balances with the totals table
    // (vertically centred against the right-column totals block).
    const totalsBlockH = Math.max(totalsEndY - totalsStartY, totalH);
    let totalY = totalsStartY + Math.max(0, (totalsBlockH - totalH) / 2);

    // Defensive page-break: if even after the smart placement the TOTAL would
    // collide with the footer, push it to a new page (very rare — only fires
    // on huge quotes that already filled the page).
    if (totalY + totalH > pageH - 56) {
      doc.addPage();
      drawWatermark(doc, pageW, pageH, logoDataUrl, logoFmt);
      totalY = 60;
    }

    // Outer gold thin frame
    doc.setDrawColor(...ACCENT);
    doc.setLineWidth(1.2);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(totalX, totalY, totalW, totalH, 3, 3, 'FD');
    // Inner thin gold frame (refined detail)
    doc.setLineWidth(0.3);
    doc.roundedRect(totalX + 4, totalY + 4, totalW - 8, totalH - 8, 1.5, 1.5, 'D');

    // Eyebrow label "TOTAL À PAYER" (small bordeaux uppercase)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...BRAND);
    doc.text((t('sum.grand') || 'TOTAL').toUpperCase(), totalX + 18, totalY + 16, { charSpace: 2.5 });

    // Tiny gold rule under label (luxury divider)
    doc.setDrawColor(...ACCENT);
    doc.setLineWidth(0.3);
    doc.line(totalX + 18, totalY + 20, totalX + 40, totalY + 20);

    // HUGE grand-total amount in serif (the hero number)
    // Auto-shrink font size to never overflow the box (critical UX)
    doc.setFont('times', 'bold');
    doc.setTextColor(...BRAND);
    const totalText = money(s.grandTotal, lang);
    // Available width = total card width minus left padding and right padding
    const availableW = totalW - 36; // 18px left + 18px right padding
    let totalFontSize = 22;
    doc.setFontSize(totalFontSize);
    while (doc.getTextWidth(totalText) > availableW && totalFontSize > 12) {
      totalFontSize -= 1;
      doc.setFontSize(totalFontSize);
    }
    doc.text(totalText, totalX + totalW - 18, totalY + 38, { align: 'right' });

    // Caption (gold uppercase serif)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(...ACCENT);
    doc.text((t('pdf.taxes_included') || 'TOUTES TAXES INCLUSES').toUpperCase(), totalX + 18, totalY + 46, { charSpace: 2 });

    // Advance y past whichever of the two columns ends lower.
    y = Math.max(totalsEndY, totalY + totalH) + 8;

    // === FRAIS DIVERS — DÉTAILS (with notes) ===
    if (s.miscFees && s.miscNotes && s.miscNotes.trim()) {
      // White background with gold accent — matches the TOTAL box style.
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(...ACCENT);
      doc.setLineWidth(0.8);

      // Pre-measure notes at the FINAL font size (9pt) so the box height is
      // correct and our space-fit check is accurate.
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const notesText = doc.splitTextToSize(s.miscNotes.trim(), pageW - margin * 2 - 24);
      const boxH = 30 + notesText.length * 12;

      // Real-space pagination — only break when content TRULY won't fit.
      // Footer divider sits at pageH - 56; we let the box touch it (no overlap).
      const bottomLimit = pageH - 56;
      if (y + boxH > bottomLimit) {
        doc.addPage();
        drawWatermark(doc, pageW, pageH, logoDataUrl, logoFmt);
        y = 60;
        // Re-apply colours after page change (state may reset)
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(...ACCENT);
        doc.setLineWidth(0.8);
      }

      // Cream-filled rounded card with gold border
      doc.roundedRect(margin, y, pageW - margin * 2, boxH, 4, 4, 'FD');
      // Gold accent vertical bar (left)
      doc.setFillColor(...ACCENT);
      doc.rect(margin, y, 4, boxH, 'F');

      // Title (bordeaux) + amount (dark gray) row
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...BRAND);
      doc.text(
        (t('pdf.misc_details') || 'FRAIS DIVERS — DÉTAILS').toUpperCase(),
        margin + 14, y + 14
      );
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(60);
      doc.text(`${money(s.miscFees, lang)}`, pageW - margin - 14, y + 14, { align: 'right' });

      // Notes body (dark gray)
      doc.setFontSize(9);
      doc.setTextColor(70);
      doc.text(notesText, margin + 14, y + 26);
      y += boxH + 12;
    }

    // === NOTES ===
    if (sub.notes && sub.notes.trim()) {
      doc.setTextColor(60);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text((t('common.notes') || 'NOTES').toUpperCase(), margin, y);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100);
      const split = doc.splitTextToSize(sub.notes.trim(), pageW - margin * 2);
      doc.text(split, margin, y + 14);
      y += 14 + split.length * 12 + 6;
    }

    // === CONDITIONS (surtout factures; aussi affiché si présent sur soumission) ===
    if (sub.conditions && String(sub.conditions).trim()) {
      if (y > pageH - 120) {
        doc.addPage();
        drawWatermark(doc, pageW, pageH, logoDataUrl, logoFmt);
        y = 60;
      }
      doc.setTextColor(60);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text((t('invoice.conditions') || 'CONDITIONS').toUpperCase(), margin, y);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100);
      const condSplit = doc.splitTextToSize(String(sub.conditions).trim(), pageW - margin * 2);
      doc.text(condSplit, margin, y + 14);
      y += 14 + condSplit.length * 12 + 6;
    }

    // === SIGNATURE BLOCK (soumissions seulement) ===
    if (!isInvoice && sub.signature_data_url) {
      if (y > pageH - 200) {
        doc.addPage();
        drawWatermark(doc, pageW, pageH, logoDataUrl, logoFmt);
        y = 60;
      }
      doc.setFillColor(240, 248, 240);
      doc.roundedRect(margin, y, pageW - margin * 2, 110, 6, 6, 'F');
      doc.setDrawColor(120, 180, 120);
      doc.setLineWidth(0.5);
      doc.roundedRect(margin, y, pageW - margin * 2, 110, 6, 6, 'S');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(30, 90, 30);
      doc.text('✓ ' + (t('pdf.signed_on') || 'SIGNÉ LE'), margin + 12, y + 16);
      try {
        // Auto-detect signature image format (PNG/JPEG/WEBP) to avoid jsPDF errors
        // when a saved signature is encoded as JPEG. Hard-coding 'PNG' caused PDF
        // generation to throw on accepted/signed submissions in some browsers.
        const sigFmt = inferImageFormat(sub.signature_data_url);
        doc.addImage(sub.signature_data_url, sigFmt, margin + 12, y + 24, 220, 60, undefined, 'FAST');
      } catch (e) { console.warn('[pdf] signature image add failed:', e); }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(40);
      doc.text(sub.signed_by_name || sub.client_name || '', margin + 250, y + 50);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100);
      const signedDate = sub.signed_at
        ? (lang === 'en'
            ? new Date(sub.signed_at).toLocaleString('en-CA', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })
            : new Date(sub.signed_at).toLocaleString('fr-CA'))
        : '';
      doc.text(signedDate, margin + 250, y + 64);
      y += 124;
    }

    // === PHOTOS PAGE (soumissions seulement) ===
    const photos = (!isInvoice && Array.isArray(sub.photos)) ? sub.photos : [];
    if (photos.length > 0) {
      doc.addPage();
      drawWatermark(doc, pageW, pageH, logoDataUrl, logoFmt);

      doc.setFillColor(...BRAND);
      doc.rect(0, 0, pageW, 60, 'F');
      doc.setFillColor(...ACCENT);
      doc.rect(0, 60, pageW, 3, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(255);
      doc.text('PHOTOS DE CHANTIER', margin, 40);
      doc.setFontSize(9);
      doc.setTextColor(220);
      doc.text(docNumber, pageW - margin, 40, { align: 'right' });

      const CAT_LABEL = { before: 'Avant', after: 'Après', measurement: 'Mesures', defect: 'Défauts' };
      let py = 90;
      const cols = 2;
      const gap = 12;
      const imgW = (pageW - margin * 2 - gap) / cols;
      const imgH = imgW * 0.75;

      for (const cat of ['before', 'after', 'measurement', 'defect']) {
        const inCat = photos.filter((p) => p.category === cat);
        if (!inCat.length) continue;
        if (py > pageH - 150) {
          doc.addPage();
          drawWatermark(doc, pageW, pageH, logoDataUrl, logoFmt);
          py = 60;
        }
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(...BRAND);
        doc.text(CAT_LABEL[cat] || cat, margin, py);
        py += 10;

        for (let i = 0; i < inCat.length; i++) {
          const col = i % cols;
          const row = Math.floor(i / cols);
          if (col === 0 && row > 0) py += imgH + 18;
          if (py + imgH > pageH - 60) {
            doc.addPage();
            drawWatermark(doc, pageW, pageH, logoDataUrl, logoFmt);
            py = 60;
          }
          const x = margin + col * (imgW + gap);
          try {
            const dataUrl = await loadImageAsDataUrl(inCat[i].url);
            if (dataUrl) {
              const fmt = inferImageFormat(dataUrl);
              doc.addImage(dataUrl, fmt, x, py, imgW, imgH, undefined, 'FAST');
            }
          } catch (e) { console.warn('[pdf] project photo add failed:', e); }
          if (inCat[i].caption) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(80);
            doc.text(inCat[i].caption.substring(0, 50), x, py + imgH + 10);
          }
        }
        py += imgH + 30;
      }
    }

    // === FOOTER on all pages ===
    const totalPages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      drawFooter(doc, pageW, pageH, profile, t, p, totalPages);
    }

    if (opts && opts.print) {
      doc.autoPrint();
      if (await sharePdfOnCapacitor(doc, fileBase)) return true;
      const blobUrl = doc.output('bloburl');
      const w = window.open(blobUrl, '_blank');
      if (!w) doc.save(`${fileBase}.pdf`);
    } else if (opts && opts.preview) {
      // Sur iOS natif, la share sheet "Génération du contenu…" bloque l'aperçu.
      // On affiche le PDF dans un overlay in-app à la place.
      if (await previewPdfOnCapacitor(doc, fileBase)) return true;
      const blobUrl = doc.output('bloburl');
      const w = window.open(blobUrl, '_blank');
      if (!w) doc.save(`${fileBase}.pdf`);
    } else if (opts && opts.returnBlob) {
      return doc.output('blob');
    } else if (opts && opts.returnDoc) {
      // Caller wants the raw jsPDF instance (to extract base64, etc.) without
      // triggering a download. Used by SignPage to email a signed copy.
      return doc;
    } else {
      if (await sharePdfOnCapacitor(doc, fileBase)) return true;
      doc.save(`${fileBase}.pdf`);
    }
    return true;
  } catch (e) {
    console.error('PDF error', e);
    return false;
  }
}

async function writePdfToCache(doc, fileBase) {
  const { Filesystem, Directory } = await import('@capacitor/filesystem');
  const dataUri = doc.output('datauristring');
  const base64 = dataUri.substring(dataUri.indexOf(',') + 1);
  const fileName = `${fileBase || 'document'}.pdf`;
  const written = await Filesystem.writeFile({
    path: `soumissionpro/${fileName}`,
    data: base64,
    directory: Directory.Cache,
    recursive: true,
  });
  return { written, fileName };
}

/**
 * Aperçu PDF in-app sur Capacitor (évite la share sheet iOS qui reste
 * bloquée sur « Génération du contenu… »).
 */
async function previewPdfOnCapacitor(doc, fileBase) {
  try {
    const platformMod = await import('./platform');
    if (!platformMod.isCapacitorNative()) return false;

    const { Capacitor } = await import('@capacitor/core');
    const { written, fileName } = await writePdfToCache(doc, fileBase);
    const src = Capacitor.convertFileSrc(written.uri);

    document.getElementById('sp-pdf-preview-overlay')?.remove();

    const overlay = document.createElement('div');
    overlay.id = 'sp-pdf-preview-overlay';
    overlay.setAttribute(
      'style',
      'position:fixed;inset:0;z-index:99999;background:#1a1412;display:flex;flex-direction:column;'
    );

    const toolbar = document.createElement('div');
    toolbar.setAttribute(
      'style',
      'display:flex;align-items:center;gap:8px;padding:12px 14px;padding-top:max(12px,env(safe-area-inset-top));background:#7A1F2B;flex-shrink:0;'
    );

    const title = document.createElement('div');
    title.textContent = fileName;
    title.setAttribute(
      'style',
      'color:#fff;font-weight:700;font-size:13px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;min-width:0;'
    );

    const shareBtn = document.createElement('button');
    shareBtn.type = 'button';
    shareBtn.textContent = 'Partager';
    shareBtn.setAttribute(
      'style',
      'background:transparent;color:#C9A961;border:1.5px solid #C9A961;border-radius:10px;padding:9px 12px;font-weight:800;font-size:12px;flex-shrink:0;'
    );
    shareBtn.onclick = async () => {
      try {
        const { Share } = await import('@capacitor/share');
        await Share.share({
          title: fileName,
          files: [written.uri],
          dialogTitle: 'SoumissionPro',
        });
      } catch (e) {
        const msg = (e && e.message ? e.message : '').toLowerCase();
        if (!msg.includes('cancel') && !msg.includes('dismiss')) {
          console.warn('[pdf] preview share failed:', e);
        }
      }
    };

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.textContent = 'Fermer';
    closeBtn.setAttribute(
      'style',
      'background:#C9A961;color:#5A1620;border:0;border-radius:10px;padding:9px 14px;font-weight:800;font-size:12px;flex-shrink:0;'
    );
    closeBtn.onclick = () => overlay.remove();

    toolbar.appendChild(title);
    toolbar.appendChild(shareBtn);
    toolbar.appendChild(closeBtn);

    const frame = document.createElement('iframe');
    frame.title = fileName;
    frame.src = src;
    frame.setAttribute(
      'style',
      'flex:1;width:100%;border:0;background:#fff;'
    );

    overlay.appendChild(toolbar);
    overlay.appendChild(frame);
    document.body.appendChild(overlay);
    return true;
  } catch (e) {
    console.warn('[pdf] capacitor preview failed, falling back:', e);
    return false;
  }
}

/**
 * iOS Capacitor WebView blocks window.open(blobUrl) AND doc.save() (no Files
 * download). Write the PDF to cache then open the system share sheet so the
 * user can Save to Files / Mail / Print.
 *
 * Returns true if handled by Capacitor, false if running on plain web.
 * @param {string} fileBase — filename without .pdf
 */
async function sharePdfOnCapacitor(doc, fileBase) {
  try {
    // Lazy-load so the web build doesn't include native bridge code.
    const platformMod = await import('./platform');
    if (!platformMod.isCapacitorNative()) return false;

    const { Share } = await import('@capacitor/share');
    const { written, fileName } = await writePdfToCache(doc, fileBase);

    // Prefer `files` (iOS) — plain `url` alone often hangs on
    // « Génération du contenu… » in the share sheet.
    await Share.share({
      title: fileName,
      files: [written.uri],
      dialogTitle: 'SoumissionPro',
    });
    return true;
  } catch (e) {
    // User dismissed share sheet — not an error.
    const msg = (e && e.message ? e.message : '').toLowerCase();
    if (msg.includes('cancel') || msg.includes('dismiss')) return true;
    console.warn('[pdf] capacitor share failed, falling back to web flow:', e);
    return false;
  }
}
